// src/components/leader/CompetitionBadges.js
// Registration state of a competition as leaders need it (audit FL-21):
// "Registrace otevřena" / "Registrace od 1. 12. 2026" / "Registrace uzavřena" and the
// deadline with a countdown that turns amber in the last 7 days. A round whose registration
// deadline has passed is "Registrace uzavřena" even while its status is still "active".
// Capacity: only "Kapacita naplněna" - groups never see how many places are taken or left
// (the organiser's wish, 13. 10. 2026; the API sends just ``isFull``).
import React from 'react';
import { Clock } from 'lucide-react';
import { competitionStatusLabel, COMPETITION_TYPE_LABELS } from '../../constants/categories';
import { daysUntil, formatDate, plural, isPastCompetition, isRegistrationClosed } from '../../utils/format';

const TONES = {
  green: 'bg-green-100 text-green-800',
  blue: 'bg-blue-100 text-blue-800',
  gray: 'bg-gray-100 text-gray-700',
  slate: 'bg-slate-200 text-slate-700',
  amber: 'bg-amber-100 text-amber-900',
  purple: 'bg-purple-100 text-purple-800',
};

export function Pill({ tone = 'gray', children, className = '' }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${TONES[tone] || TONES.gray} ${className}`}>
      {children}
    </span>
  );
}

/** Label + tone of the registration state. */
export function registrationState(competition) {
  if (!competition) return { label: '', tone: 'gray' };
  // An old round left "upcoming"/"active" must not offer registration "from <past date>".
  if (isPastCompetition(competition) && !['finished', 'archived'].includes(competition.status)) {
    return { label: 'Proběhla', tone: 'slate' };
  }
  if (!['finished', 'archived'].includes(competition.status) && isRegistrationClosed(competition)) {
    return { label: 'Registrace uzavřena', tone: 'gray' };
  }
  switch (competition.status) {
    case 'active':
      return { label: 'Registrace otevřena', tone: 'green' };
    case 'upcoming':
      return competition.registrationStart
        ? { label: `Registrace od ${formatDate(competition.registrationStart)}`, tone: 'blue' }
        : { label: 'Registrace zatím není otevřena', tone: 'blue' };
    case 'closed':
      return { label: 'Registrace uzavřena', tone: 'gray' };
    default:
      return { label: competitionStatusLabel(competition.status), tone: 'slate' };
  }
}

export function RegistrationBadge({ competition, className }) {
  const { label, tone } = registrationState(competition);
  if (!label) return null;
  return (
    <Pill tone={tone} className={className}>
      {label}
    </Pill>
  );
}

export function CompetitionTypeBadge({ type }) {
  if (!type) return null;
  const tone = type === 'grand_finale' ? 'amber' : type === 'final' ? 'purple' : 'blue';
  return <Pill tone={tone}>{COMPETITION_TYPE_LABELS[type] || type}</Pill>;
}

/** "za 5 dní" / "zítra" / "dnes" / null for past dates. */
export function countdownText(days) {
  if (days === null || days === undefined || days < 0) return null;
  if (days === 0) return 'dnes';
  if (days === 1) return 'zítra';
  return `za ${days} ${plural(days, ['den', 'dny', 'dní'])}`;
}

/** "Uzávěrka registrace 20. 3. 2027 (za 5 dní)", amber in the last week. */
export function DeadlineText({ date, label = 'Uzávěrka registrace', className = '' }) {
  if (!date) return null;
  const days = daysUntil(date);
  const countdown = countdownText(days);
  const urgent = days !== null && days >= 0 && days <= 7;
  return (
    <span className={`inline-flex items-center gap-1.5 ${urgent ? 'font-medium text-amber-800' : 'text-gray-700'} ${className}`}>
      <Clock className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <span>
        {label} {formatDate(date)}
        {countdown && ` (${countdown})`}
      </span>
    </span>
  );
}

/** Every place of a competition with a capacity limit is taken (``isFull`` from the API). */
export function isFull(competition) {
  return Boolean(competition && competition.isFull);
}

/** "Kapacita naplněna" of a competition whose registration is open. */
export function CapacityBadge({ competition }) {
  if (!competition || isPastCompetition(competition) || isRegistrationClosed(competition)) return null;
  if (competition.status === 'active' && isFull(competition)) return <Pill tone="gray">Kapacita naplněna</Pill>;
  return null;
}
